import type { ProductDto } from 'api/src/core/schemas/ProductSchema';
import { makeAutoObservable } from 'mobx';
import { z } from 'zod';
import extractValue, {
  type EventOrValue,
} from '../../../../../../store/utils/extractValue';
import Validator from '../../../../../../store/utils/Validator';

const DAY = 24 * 60 * 60;

export default class ProductLicense {
  static Schema = z.object({
    offlineLeaseDays: z.coerce.number().int().min(1),
    onlineLeaseDays: z.coerce.number().int().min(1),
    seatLimit: z.coerce.number().int().min(1),
  });

  enabled = false;
  offlineEnabled = false;
  offlineLeaseDays = '30';
  onlineLeaseDays = '3';
  seatLimit = '3';

  private readonly isStored: boolean;
  private readonly validator = new Validator(this, ProductLicense.Schema);

  constructor(
    dto: ProductDto['license'],
    private readonly onChange: () => void,
  ) {
    makeAutoObservable<this, 'onChange' | 'validator'>(
      this,
      { onChange: false, validator: false },
      { autoBind: true },
    );

    this.isStored = Boolean(dto);
    if (dto) {
      this.enabled = dto.enabled;
      this.offlineEnabled = dto.offlineEnabled;
      this.offlineLeaseDays = String(dto.offlineLeaseSeconds / DAY);
      this.onlineLeaseDays = String(dto.onlineLeaseSeconds / DAY);
      this.seatLimit = String(dto.seatLimit);
    }

    this.validator.validate();
  }

  get errors() {
    return this.validator.errorsForEnabledFields;
  }

  get isValid() {
    return this.validator.isValid;
  }

  handleBlur(e: EventOrValue<keyof ProductLicense>) {
    this.validator.enableFieldValidation(extractValue(e).name);
  }

  handleChange(e: EventOrValue) {
    const { name, value } = extractValue(e);
    Object.assign(this, { [name]: value });
    this.validator.validate();
    this.onChange();
  }

  toJSON(): ProductDto['license'] {
    if (!this.isStored && !this.enabled) {
      return undefined;
    }

    return {
      enabled: this.enabled,
      offlineEnabled: this.offlineEnabled,
      offlineLeaseSeconds: Math.round(Number(this.offlineLeaseDays) * DAY),
      onlineLeaseSeconds: Math.round(Number(this.onlineLeaseDays) * DAY),
      seatLimit: Number(this.seatLimit),
    };
  }
}
